import { datadogRum } from "@datadog/browser-rum";
import {
  nextjsPlugin,
  onRouterTransitionStart,
} from "@datadog/browser-rum-nextjs";

import { APP_VERSION } from "@/lib/app-version";

/**
 * Datadog Browser RUM 초기화 (SD-7923) — gf-web 신규 연동.
 *
 * instrumentation-client 는 브라우저에서 React hydration 직전 1회 실행되는 Next.js(>=15.3)
 * 컨벤션 파일이다. App Router 라우트 추적은 (1) 여기서 onRouterTransitionStart 재export 와
 * (2) 루트 레이아웃의 <DatadogAppRouter/> 가 한 쌍으로 동작한다.
 *
 * 리전 처리(gf-frontend-next 표준 이관): service 이름에 리전을 인코딩한다. 단일 이미지가 여러
 * 리전에 배포되므로 빌드타임 값(NEXT_PUBLIC_*)으로는 리전을 구분할 수 없다. 따라서 런타임
 * 라우트(/api/runtime-config)가 Cloud Run per-region --set-env-vars 로 주입된 DD_SERVICE
 * (= gf-web-<region>)를 반환하고, init *전에 await* 하여 service 를 리전별로 확정한다.
 * 실패/부재 시 빌드타임 NEXT_PUBLIC_DD_RUM_SERVICE 로 폴백한다. env 는 환경 단위라 빌드타임
 * 값(production/testbed)으로 충분하다.
 *
 * applicationId/clientToken 이 비면(예: 로컬 dev) init 을 건너뛰어 RUM 노이즈를 만들지 않는다.
 * RUM client token 은 본래 브라우저에 노출되는 공개값이므로 NEXT_PUBLIC 노출이 정상이다.
 */

// App Router 클라이언트 네비게이션마다 Next.js 가 호출 → 플러그인이 라우트 전환을 추적한다.
export { onRouterTransitionStart };

const applicationId = process.env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID;
const clientToken = process.env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN;

/**
 * 멀티 도메인 구분. payments.golffix.io 와 www.golffix.io 는 같은 gf-web 배포를 공유하므로
 * 도메인은 서버 env 가 아니라 브라우저 hostname 으로만 알 수 있다. 또 payments 호스트는
 * `/payments` 접두어를 rewrite 로 숨겨 루트로 서빙하므로(proxy.ts) RUM 경로(@view.url_path)로는
 * 구분이 안 된다 → web_surface 속성으로 분류해 @context.web_surface 페이싯을 만든다.
 * (proxy.ts PAYMENTS_HOST 규칙과 동일: payments. 서브도메인 계열)
 */
function resolveWebSurface(): "payments" | "www" {
  if (typeof window === "undefined") return "www";
  return /(^|\.)payments\./i.test(window.location.hostname)
    ? "payments"
    : "www";
}

/** 런타임 설정(/api/runtime-config)에서 리전 포함 DD_SERVICE 를 가져온다. 실패 시 undefined. */
async function fetchRuntimeDdService(): Promise<string | undefined> {
  try {
    const res = await fetch("/api/runtime-config");
    if (!res.ok) return undefined;
    const data = (await res.json()) as { ddService?: string | null };
    return data.ddService ?? undefined;
  } catch {
    return undefined;
  }
}

async function initRum(): Promise<void> {
  if (!applicationId || !clientToken) return;

  // 런타임 DD_SERVICE(리전 포함) 우선, 부재 시 빌드타임 값 → 최종 폴백 "gf-web".
  const service =
    (await fetchRuntimeDdService()) ||
    process.env.NEXT_PUBLIC_DD_RUM_SERVICE ||
    "gf-web";

  // 수집률은 env 차등: testbed(tb) 는 디버깅 위해 전수, prod(pr) 는 비용 고려해 낮춘다.
  // *testbed-positive* 매칭이라 env 누락/오타는 안전하게 prod 값으로 떨어진다(prod 비용 급증 방지).
  // prod 번들에선 NEXT_PUBLIC_DD_RUM_ENV 가 "pr" 로 인라인 → 상수 폴딩되어 기존 값과 동일.
  const isTestbed = process.env.NEXT_PUBLIC_DD_RUM_ENV === "tb";

  try {
    datadogRum.init({
      applicationId,
      clientToken,
      site: process.env.NEXT_PUBLIC_DD_RUM_SITE || "datadoghq.com",
      service,
      env: process.env.NEXT_PUBLIC_DD_RUM_ENV,
      version: APP_VERSION,
      // 세션 수집률·리플레이율 env 차등(리플레이가 비용 대부분이라 prod 는 낮게 유지).
      // prod: 세션 10%·그중 20%(=전체 2%) 리플레이. testbed: 세션 50%·그중 50%(=전체 25%) 리플레이.
      sessionSampleRate: isTestbed ? 50 : 30,
      sessionReplaySampleRate: isTestbed ? 50 : 15,
      // 분산추적 hop ① (SD-7125): 브라우저는 same-origin /api/* 만 호출하므로 gf-web 자기
      // 호스트(www/payments + testbed)에 trace 헤더를 주입한다. same-origin 이라 CORS preflight
      // 부담이 없다. 단, 이것만으론 부족 — 받는 Next 서버(instrumentation.ts dd-trace)가 헤더를
      // 수용·전파해야 RUM↔APM 이 연결된다(hop ②). propagatorTypes 는 백엔드 tracer 스타일과
      // 일치해야 한다(W3C tracecontext 표준 채택, DD_TRACE_PROPAGATION_STYLE 정합 확인 필요).
      allowedTracingUrls: [
        {
          match: /^https:\/\/(test\.)?(www|payments)\.golffix\.io\//,
          propagatorTypes: ["tracecontext"],
        },
      ],
      // RUM 세션 샘플과 별개로, APM 트레이스를 생성/연결하는 비율(APM 인제스천 비용 직결).
      traceSampleRate: isTestbed ? 100 : 20,
      trackResources: true,
      trackUserInteractions: true,
      trackLongTasks: true,
      // 이용권 PIN 등 민감 입력이 세션 리플레이에 남지 않도록 입력값을 마스킹한다.
      defaultPrivacyLevel: "mask-user-input",
      plugins: [nextjsPlugin()],
    });

    // 도메인(payments/www)을 모든 RUM 이벤트에 부착 → @context.web_surface 로 각각 필터.
    // 리전(service)·env 와 직교하는 차원이라 글로벌 컨텍스트로 둔다.
    datadogRum.setGlobalContextProperty("web_surface", resolveWebSurface());
  } catch (error) {
    // 관측성 초기화 실패가 앱 부팅을 막지 않도록 삼킨다(Next.js 문서 권고).
    console.error("[datadog-rum] init failed", error);
  }
}

void initRum();
